import { useEffect } from "react";
import type { Article } from "@/types/portal";
import { CategoryBadge } from "./bits";

export default function ArticleModal({
  article,
  onClose,
}: {
  article: Article;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-foreground-950/60 px-4 py-6 backdrop-blur-sm md:py-12"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-background-50 shadow-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-52 w-full overflow-hidden md:h-72">
          <img src={article.image} alt={article.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent" />
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭阅读窗"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition hover:bg-background-50/40"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <CategoryBadge slug={article.cat} />
          </div>
        </div>

        <article className="px-5 py-6 md:px-8 md:py-8">
          <h3 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[28px]">
            {article.title}
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" /> {article.date} {article.time}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" /> {article.reads} 阅读
            </span>
            <span className="inline-flex items-center gap-1 text-primary-600">
              <i className="ri-shield-check-line" /> {article.source}
            </span>
          </div>

          <div className="mt-6 space-y-4">
            {article.body.map((p, idx) => (
              <p key={idx} className="text-[15px] leading-8 text-foreground-700">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-7 rounded-lg border border-background-200 bg-background-100/70 p-4 text-xs leading-relaxed text-foreground-500">
            <span className="font-medium text-foreground-700">编辑部说明：</span>
            本内容基于公开信源与从业者披露整理。凡处于“疑似 / 待核实”阶段的信息，均已明确标注，请以司法机关及权威官方通报为准。我们不针对具体个人作主观定性。
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-foreground-800"
          >
            返回瓜闻列表
            <i className="ri-arrow-right-line" />
          </button>
        </article>
      </div>
    </div>
  );
}